import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Card } from '~/eds/src/components/Card/Card';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';

export interface CardFooterActionsProps {
  hasActiveIntegration: boolean;
  hasError: boolean;
  isConnected: boolean;
  isLoading: boolean;
  isUnchanged: boolean;
  disabled?: boolean;
  onCancel: () => void;
  onConnect: () => void;
  onReconnect: () => void;
  onUpdate: () => void;
}

export function CardFooterActions({
  hasActiveIntegration,
  hasError,
  isConnected,
  isUnchanged,
  isLoading,
  disabled = false,
  onCancel,
  onConnect,
  onReconnect,
  onUpdate,
}: CardFooterActionsProps) {
  const intl = useIntl();

  const footerActions = useMemo(() => {
    const actions: UserAction[] = [
      {
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Cancel',
          defaultMessage: 'Cancel',
          description: 'Cancel button text',
        }),
        level: 'tertiary',
        disabled: disabled || isLoading || isUnchanged,
        onClick: onCancel,
      },
    ];

    if (!isConnected && !hasError) {
      actions.push({
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Connect',
          defaultMessage: 'Connect',
          description: 'Button text to connect an integration',
        }),
        level: 'primary',
        disabled: disabled || isLoading || hasActiveIntegration,
        isLoading: isLoading,
        onClick: onConnect,
      });
    } else if (!isUnchanged) {
      // User has made changes - always show Update button (even if there's an error)
      actions.push({
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Update',
          defaultMessage: 'Update',
          description: 'Update button text',
        }),
        level: 'primary',
        disabled: disabled || isLoading,
        isLoading: isLoading,
        onClick: onUpdate,
      });
    } else if (hasError) {
      // No changes but there's an error - show Reconnect button
      actions.push({
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Reconnect',
          defaultMessage: 'Reconnect',
          description: 'Reconnect button text',
        }),
        level: 'primary',
        disabled: disabled || isLoading,
        isLoading: isLoading,
        onClick: onReconnect,
      });
    } else {
      // No changes and no error - show disabled Update button
      actions.push({
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Update',
          defaultMessage: 'Update',
          description: 'Update button text',
        }),
        level: 'primary',
        disabled: disabled || isUnchanged || isLoading,
        isLoading: isLoading,
        onClick: onUpdate,
      });
    }

    return actions;
  }, [
    disabled,
    hasActiveIntegration,
    hasError,
    intl,
    isConnected,
    isLoading,
    isUnchanged,
    onCancel,
    onConnect,
    onReconnect,
    onUpdate,
  ]);

  const footer = {
    actions: footerActions,
    leftContent:
      (isConnected || hasError) && !isUnchanged ? (
        <Text variant="body" color="text.quiet">
          {intl.formatMessage({
            id: 'NewESignature.ESignatureIntegrationCard.InstructionToUpdate',
            defaultMessage: 'Click on "Update" to save changes',
            description: 'Instruction to update the e-signature integration',
          })}
        </Text>
      ) : null,
  };

  return (
    <Card mode="embedded" footer={footer}>
      {null}
    </Card>
  );
}
